import { html, css, LitElement, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import {
  DEFAULT_EAD_DIAGRAM_STANDARD,
  getEffectiveDiagramStandard,
  isUsingDefaultDiagramStandard,
  resetStoredDiagramStandard,
  saveStoredDiagramStandard,
} from "../ead-diagram-standard.ts";

@customElement("ead-diagram-standard-view")
export class EadDiagramStandardView extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
    }
    .container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .header {
      padding: 20px 28px 16px;
      border-bottom: 1px solid var(--border, #2e2f33);
    }
    .header h2 {
      margin: 0 0 6px;
      font-size: 18px;
      font-weight: 600;
    }
    .header p {
      margin: 0;
      font-size: 13px;
      color: var(--muted, #8b8d93);
      line-height: 1.5;
      max-width: 720px;
    }
    .content {
      flex: 1;
      padding: 24px 28px;
      overflow-y: auto;
    }
    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }
    .toolbar h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
    }
    .toolbar-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .badge {
      display: inline-block;
      margin-left: 8px;
      padding: 2px 8px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 500;
      color: var(--muted, #8b8d93);
      border: 1px solid var(--border, #2e2f33);
    }
    .btn {
      padding: 6px 14px;
      border-radius: 6px;
      border: 1px solid var(--border, #2e2f33);
      background: transparent;
      color: var(--fg, #e4e5e7);
      font-size: 12px;
      cursor: pointer;
    }
    .btn:hover {
      background: rgba(255, 255, 255, 0.05);
    }
    .btn-primary {
      background: var(--accent, #6ea8fe);
      color: #fff;
      border-color: var(--accent, #6ea8fe);
    }
    .preview {
      background: rgba(30, 31, 34, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 8px;
      padding: 20px;
      font-size: 14px;
      line-height: 1.7;
      min-height: 240px;
    }
    .editor textarea {
      width: 100%;
      min-height: 360px;
      resize: vertical;
      font-family: "SF Mono", Monaco, monospace;
      font-size: 13px;
      line-height: 1.6;
      padding: 12px;
      border-radius: 8px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg-surface, #222326);
      color: var(--fg, #e4e5e7);
      box-sizing: border-box;
    }
    .note {
      margin-top: 16px;
      font-size: 12px;
      color: var(--muted, #8b8d93);
      line-height: 1.5;
    }
  `;

  @state() private _editing = false;
  @state() private _draft = DEFAULT_EAD_DIAGRAM_STANDARD;
  @state() private _usingDefault = true;

  connectedCallback(): void {
    super.connectedCallback();
    this._refreshFromStorage();
  }

  private _refreshFromStorage(): void {
    this._draft = getEffectiveDiagramStandard();
    this._usingDefault = isUsingDefaultDiagramStandard();
  }

  private _startEdit(): void {
    this._draft = getEffectiveDiagramStandard();
    this._editing = true;
  }

  private _cancelEdit(): void {
    this._editing = false;
    this._refreshFromStorage();
  }

  private _save(): void {
    saveStoredDiagramStandard(this._draft);
    this._editing = false;
    this._refreshFromStorage();
  }

  private _resetToDefault(): void {
    resetStoredDiagramStandard();
    this._draft = DEFAULT_EAD_DIAGRAM_STANDARD;
    this._editing = false;
    this._refreshFromStorage();
  }

  render() {
    const displayText = this._editing ? this._draft : getEffectiveDiagramStandard();

    return html`
      <div class="container">
        <div class="header">
          <h2>EAD Diagram Standard</h2>
          <p>
            Default instructions for AI-generated EAD diagrams. Per-skill Generate prompts start from
            this standard unless a skill defines its own override.
          </p>
        </div>
        <div class="content">
          <div class="toolbar">
            <h3>
              Standard prompt
              ${this._usingDefault && !this._editing
                ? html`<span class="badge">Default</span>`
                : nothing}
            </h3>
            <div class="toolbar-actions">
              ${this._editing
                ? html`
                    <button class="btn btn-primary" @click=${this._save}>Save</button>
                    <button class="btn" @click=${this._cancelEdit}>Cancel</button>
                  `
                : html`
                    <button class="btn" @click=${this._startEdit}>Edit</button>
                    ${!this._usingDefault
                      ? html`<button class="btn" @click=${this._resetToDefault}>Reset to default</button>`
                      : nothing}
                  `}
            </div>
          </div>
          ${this._editing
            ? html`
                <div class="editor">
                  <textarea
                    .value=${this._draft}
                    @input=${(e: Event) => {
                      this._draft = (e.target as HTMLTextAreaElement).value;
                    }}
                  ></textarea>
                </div>
              `
            : html`
                <div class="preview">
                  ${unsafeHTML(toSanitizedMarkdownHtml(displayText))}
                </div>
              `}
          <p class="note">
            Grammar: <code>[Entity]</code>, <code>.[Sub Entity]</code>, <code>&lt;Action&gt;</code>,
            <code>--&gt;</code>, and <code>{Condition}</code>. Keep diagrams simple — at most 10 entity
            nodes and 12 action nodes.
          </p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-diagram-standard-view": EadDiagramStandardView;
  }
}
